import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldAlert, Search, Eye, Ban, CheckCircle2, LogOut, RefreshCcw,
  Loader2, AlertTriangle, X, Monitor, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SECURITY_ADMIN } from "@/constants/testIds";
import { LeakCheck } from "@/components/admin/LeakCheck";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function DeviceLabel({ ua }) {
  if (!ua) return <span className="text-slate-400">Unknown</span>;
  const short = /Chrome\/\d/.test(ua) ? "Chrome"
    : /Firefox\/\d/.test(ua) ? "Firefox"
    : /Safari\/\d/.test(ua) && !/Chrome/.test(ua) ? "Safari"
    : /Edge\/\d/.test(ua) ? "Edge"
    : "Browser";
  const os = /Windows/.test(ua) ? "Windows"
    : /Macintosh|Mac OS X/.test(ua) ? "macOS"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS"
    : /Linux/.test(ua) ? "Linux"
    : "";
  return <span title={ua}>{short}{os ? ` · ${os}` : ""}</span>;
}

/* -------------------------------------------------------------------------- */

function UserDetailModal({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspending, setSuspending] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/security/users/${userId}/detail`);
      setData(r.data);
    } catch { toast.error("Couldn't load user"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId]);  // eslint-disable-line

  const doRevokeAll = async () => {
    if (!window.confirm("Force-logout ALL of this user's active sessions?")) return;
    try {
      const r = await api.post(`/admin/security/users/${userId}/revoke-all`);
      toast.success(`Revoked ${r.data.revoked} session${r.data.revoked === 1 ? "" : "s"}`);
      onChanged?.();
      await load();
    } catch { toast.error("Revoke failed"); }
  };

  const doForceOtp = async () => {
    try {
      await api.post(`/admin/security/users/${userId}/force-otp`);
      toast.success("OTP will be required on this user's next login");
      await load();
    } catch { toast.error("Couldn't force OTP"); }
  };

  const doRevokeOne = async (sid) => {
    try {
      await api.post(`/admin/security/sessions/${sid}/revoke`);
      toast.success("Session revoked");
      await load();
    } catch { toast.error("Revoke failed"); }
  };

  const doSuspend = async () => {
    if (!reason.trim()) { toast.error("Give a reason for the audit log"); return; }
    setSuspending(true);
    try {
      await api.post(`/admin/security/users/${userId}/suspend`, { reason: reason.trim() });
      toast.success("Account suspended");
      setReason("");
      onChanged?.();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Suspend failed"); }
    finally { setSuspending(false); }
  };

  const doUnsuspend = async () => {
    try {
      await api.post(`/admin/security/users/${userId}/unsuspend`);
      toast.success("Account re-enabled");
      onChanged?.();
      await load();
    } catch { toast.error("Unsuspend failed"); }
  };

  return (
    <div
      data-testid={SECURITY_ADMIN.detailModal}
      className="fixed inset-0 z-[75] bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen flex items-start justify-center py-8 px-4">
        <div
          className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                Security detail
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900">
                {loading ? "Loading…" : data?.user.email}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center"
            >
              <X size={16} />
            </button>
          </div>

          {loading || !data ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Distinct IPs (7d)", value: data.metrics.distinct_ip_count, flag: data.metrics.flagged },
                  { label: "Devices (7d)", value: data.metrics.distinct_device_count },
                  { label: "Logins (7d)", value: data.metrics.event_count },
                ].map((m) => (
                  <div
                    key={m.label}
                    className={`rounded-xl border p-4 ${m.flag ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-slate-50"}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                      {m.label}
                    </div>
                    <div className={`mt-1 font-display font-black text-3xl tabular-nums ${m.flag ? "text-rose-700" : "text-slate-900"}`}>
                      {m.value}
                    </div>
                    {m.flag && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-rose-700">
                        <AlertTriangle size={10} /> Flagged
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  Enforcement
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={doRevokeAll}
                    data-testid={SECURITY_ADMIN.revokeAllButton}
                    variant="outline"
                    className="border-slate-300"
                  >
                    <LogOut size={13} className="mr-1.5" /> Force logout all devices
                  </Button>
                  <Button
                    onClick={doForceOtp}
                    data-testid="admin-security-force-otp-button"
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <ShieldAlert size={13} className="mr-1.5" /> Require OTP on next login
                  </Button>
                  {data.user.suspended ? (
                    <Button
                      onClick={doUnsuspend}
                      data-testid={SECURITY_ADMIN.unsuspendButton}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    >
                      <CheckCircle2 size={13} className="mr-1.5" /> Re-enable account
                    </Button>
                  ) : null}
                </div>
                {data.user.suspended ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-900 text-sm p-3">
                    <div className="font-semibold inline-flex items-center gap-1.5">
                      <Ban size={13} /> Suspended
                    </div>
                    <div className="text-xs mt-0.5">
                      {data.user.suspended_reason || "No reason recorded"} · {formatDate(data.user.suspended_at)}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason for suspension (visible only in audit log)"
                      className="h-10 flex-1"
                    />
                    <Button
                      onClick={doSuspend}
                      disabled={suspending}
                      data-testid={SECURITY_ADMIN.suspendButton}
                      className="bg-rose-600 hover:bg-rose-500 text-white"
                    >
                      {suspending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Ban size={13} className="mr-1.5" />}
                      Suspend account
                    </Button>
                  </div>
                )}
              </div>

              {/* Sessions */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-flex items-center gap-1.5">
                  <Monitor size={12} /> Sessions ({data.sessions.length})
                </div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Device</th>
                        <th className="text-left px-3 py-2 font-semibold">IP</th>
                        <th className="text-left px-3 py-2 font-semibold">Created</th>
                        <th className="text-left px-3 py-2 font-semibold">Last seen</th>
                        <th className="text-left px-3 py-2 font-semibold">Status</th>
                        <th className="text-right px-3 py-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.sessions.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-sm">No sessions yet</td></tr>
                      ) : data.sessions.map((s) => (
                        <tr key={s.sid} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-700 text-xs">
                            <DeviceLabel ua={s.user_agent} />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{s.last_ip || s.ip}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{formatDate(s.created_at)}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{formatDate(s.last_seen_at)}</td>
                          <td className="px-3 py-2 text-xs">
                            {s.revoked_at ? (
                              <span className="inline-flex items-center gap-1 text-slate-500">
                                <Ban size={10} /> revoked
                                <span className="text-slate-400">({s.revoke_reason || "-"})</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {!s.revoked_at && (
                              <button
                                type="button"
                                onClick={() => doRevokeOne(s.sid)}
                                className="text-xs font-semibold text-rose-600 hover:text-rose-500"
                              >
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent events */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-flex items-center gap-1.5">
                  <Clock size={12} /> Recent login activity
                </div>
                <div className="rounded-lg border border-slate-200 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-slate-100">
                      {data.events.length === 0 ? (
                        <tr><td className="px-3 py-3 text-slate-500 text-center">No events</td></tr>
                      ) : data.events.map((e, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-500 w-32">{formatDate(e.created_at)}</td>
                          <td className="px-3 py-2 font-mono">{e.ip}</td>
                          <td className="px-3 py-2 text-slate-700"><DeviceLabel ua={e.user_agent} /></td>
                          <td className="px-3 py-2">
                            <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
                              e.kind === "login_success" ? "bg-emerald-100 text-emerald-700" :
                              e.kind === "login_fail" ? "bg-rose-100 text-rose-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {e.kind.replace("_", " ")}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export default function AdminSecurity() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openedUser, setOpenedUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.get("/admin/security/watchlist"),
        api.get("/admin/security/config"),
      ]);
      setItems(w.data.items || []);
      setConfig(c.data);
    } catch { toast.error("Couldn't load security data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((i) =>
      (i.email || "").toLowerCase().includes(term)
      || (i.ip_sample || []).some((ip) => ip.includes(term))
    );
  }, [items, q]);

  const flaggedCount = items.filter((i) => i.flagged).length;

  return (
    <div data-testid={SECURITY_ADMIN.root} className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">
          Security · Watchlist
        </div>
        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
          Account sharing controls
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Every login is logged with IP + device. Accounts logging in from{" "}
          {config?.watchlist_ip_threshold ?? "N"}+ distinct IPs in{" "}
          {config?.watchlist_window_days ?? "N"} days are flagged for your review.
          Concurrent-session cap is <b>{config?.max_concurrent_sessions ?? "N"}</b> per account.
        </p>
      </div>
      <LeakCheck />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-5">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-700">
            Flagged now
          </div>
          <div className="mt-1 font-display font-black text-4xl text-rose-700 tabular-nums">
            {flaggedCount}
          </div>
          <div className="mt-1 text-xs text-rose-800/70">
            Accounts hitting IP threshold
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">
            Tracked (7d)
          </div>
          <div className="mt-1 font-display font-black text-4xl text-slate-900 tabular-nums">
            {items.length}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Users active in the window
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700">
            Session cap
          </div>
          <div className="mt-1 font-display font-black text-4xl text-blue-800 tabular-nums">
            {config?.max_concurrent_sessions ?? "—"}
          </div>
          <div className="mt-1 text-xs text-blue-800/70">
            Devices per account
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or IP…"
            data-testid={SECURITY_ADMIN.search}
            className="h-11 pl-9"
          />
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="border-slate-300"
        >
          <RefreshCcw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Watchlist table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShieldAlert size={28} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-600 font-semibold">
              No login activity in the window yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Member</th>
                  <th className="text-left px-4 py-3 font-semibold">Distinct IPs</th>
                  <th className="text-left px-4 py-3 font-semibold">Devices</th>
                  <th className="text-left px-4 py-3 font-semibold">Logins</th>
                  <th className="text-left px-4 py-3 font-semibold">Last login</th>
                  <th className="text-right px-4 py-3 font-semibold">Review</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr
                    key={row.user_id}
                    data-testid={`${SECURITY_ADMIN.row}-${row.user_id}`}
                    className={`hover:bg-slate-50 transition-colors ${row.flagged ? "bg-rose-50/50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {row.flagged && (
                          <AlertTriangle size={14} className="text-rose-500 shrink-0" />
                        )}
                        <div>
                          <div className="font-semibold text-slate-900">{row.email}</div>
                          <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                            {row.ip_sample?.slice(0, 3).join(" · ")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono font-bold tabular-nums ${row.flagged ? "text-rose-700" : "text-slate-900"}`}>
                        {row.distinct_ip_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-slate-700">
                      {row.distinct_device_count}
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-slate-700">
                      {row.event_count}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {formatDate(row.last_login_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        onClick={() => setOpenedUser(row.user_id)}
                        data-testid={`${SECURITY_ADMIN.reviewButton}-${row.user_id}`}
                        size="sm"
                        variant="outline"
                        className="border-slate-300"
                      >
                        <Eye size={12} className="mr-1.5" /> Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openedUser && (
        <UserDetailModal
          userId={openedUser}
          onClose={() => setOpenedUser(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
